import React, {FC, useContext, useEffect, useState} from 'react';
import {Route, Routes, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../reducers";
import FetchData from "../../../components/Fetch/FetchData";
import classNames from "classnames";
import MenuComponent from "../components/MenuComponent";
import CartButtonComponent from "../../../components/Cart/components/CartButton/CartButtonComponent";
import CartStickyComponent from "../../../components/Cart/CartStickyComponent";
import AgeRestrictedModalComponent from "../components/AgeRestricted/AgeRestrictedModalComponent";
import MenuAdvertComponent from "../components/Advert/MenuAdvertComponent";
import {getStoreConfig, getStoreDefaultConfig, getStoreLayoutComponentsConfig} from "../../../services/Store/reducer";
import AppConfig from "../../../services/AppConfig";
import {usePrefix} from "../../../services/Prefix";
import StoreUtils from "../../../services/Store/StoreUtils";
import CartStickyContextPage from "../../Contexts/CartStickyContextPage";
import IframeWidget from "../../../utils/IframeWidget";
import {ReactComponent as ArrowTopSVG} from "../../../../images/icons/arrow-top.svg";
import {useTranslation} from "react-i18next";
import MenuPromotionsComponent from "../components/Promotion/MenuPromotionsComponent";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions,
    getUpsells
} from "../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../services/Session/reducer";
import {getCart} from "../../../services/Cart/reducer";
import {VoucherProductsContext} from "../../../services/VoucherProducts/VoucherProductsContext";
import VoucherProductsModal from "../../../components/Modal/VoucherProductsModal";
import ResponsiveComponent from "../../../components/Common/ResponsiveComponent";
import {MenuSearchContext} from "../../../services/Menu/services/Search/MenuSearchContext";
import RedirectOldMenuAdd from "../components/Navigation/RedirectOldMenuAdd";
import MenuCategoryPage from "./Category/MenuCategoryPage";
import MenuAdvertBanner from "../components/Advert/MenuAdvertBanner";
import MenuProductPage from "./Product/MenuProductPage";
import {OrderType, UpsellApi} from "../../../services/Api/types";
import UpsellHelper from "../../../services/Menu/services/Upsell/UpsellHelper";
import UpsellsModal from "../../../components/Cart/components/Upsell/UpsellsModal";
import CallWaiterStickyButton from "../components/CallWaiterStickyButton";
import Hero from "../../../components/Common/Hero";
import {isAnotherDateAvailable} from "../../../components/Cart/utils";
import {getParsedSessionOrderType} from "../../../utils/order";
import UnavailablePickupAtModal from "../../../components/Modal/UnavailablePickupAtModal";
import UnavailableOrderTypeModal from "../../../components/Modal/UnavailableOrderTypeModal";

type Props = ReturnType<typeof mapStateToProps> & DispatchProp;

const MenuPage: FC<Props> = (props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const [isScrollToTopButtonVisible, setScrollToTopButtonVisible] = useState(false);
    const {
        showVoucherProductsModal,
        setShowVoucherProductsModal,
    } = useContext(VoucherProductsContext);
    const {showSearchBar} = useContext(MenuSearchContext);
    const menuAdverts = props.storeLayoutComponentsConfig ? props.storeLayoutComponentsConfig.filter(x => x.placement == "menu_advert") : [];
    const isMultilevelMenu = StoreUtils.isMultilevelMenu(props.config);
    const {menuId} = useParams();
    const sortedMenus = [...props.menus].sort((a: Record<string, any>, b: Record<string, any>) => a.name.localeCompare(b.name));
    let finalMenuId = isMultilevelMenu ? menuId ?? sortedMenus[0]?.id : undefined;
    finalMenuId = finalMenuId ? Number(finalMenuId) : finalMenuId;
    const [validUpsells, setValidUpsells] = useState<UpsellApi[]>([]);
    const [hasRedirected, setHasRedirected] = useState(false);
    const [showUnavailablePickupAtModal, setShowUnavailablePickupAtModal] = useState(false);
    const [showUnavailableOrderTypeModal, setShowUnavailableOrderTypeModal] = useState(false);
    const [searchParams] = useSearchParams();

    const handleScroll = (scrollY: number) => {
        setScrollToTopButtonVisible(scrollY > 0);
    };

    useEffect(() => {
        if (AppConfig.isWidget()) {
            window.addEventListener('message', (event) => {
                const {type} = event.data;
                if (type === 'PARENT_SCROLL') {
                    handleScroll(event.data.iframeParentOffsetTop ?? 0);
                }
            });
        } else {
            window.addEventListener('scroll', () => handleScroll(window.scrollY));
            return () => window.removeEventListener('scroll', () => handleScroll(window.scrollY));
        }
    }, []);

    useEffect(() => {
        navigateViaProductSearchParams();
    }, [searchParams, props.items]);

    const navigateViaProductSearchParams = () => {
        const productName = searchParams.get('product_name');

        if (!productName) return;

        if (isMultilevelMenu) {
            const correspondingItem = props.items.find((item: Record<string, any>) => {
                const name = item.translations.find((translation: Record<string, any>) => translation.name === productName)?.name ?? item.name;

                return name === productName;
            });

            if (correspondingItem) {
                const menu = props.menus.find((menu: Record<string, any>) => menu.categories.some((menuCategory: Record<string, any>) => {
                    const fullCategoryInfo = props.categories.find((category: Record<string, any>) => category.id === menuCategory.category_id);
                    const itemInCategory = fullCategoryInfo?.entities.find((entity: Record<string, any>) => entity.entity_id === correspondingItem.id);

                    if (itemInCategory) return menu;

                    return undefined;
                }));

                if (menu) {
                    navigate(AppConfig.getLinkWithPrefix(`/menu/${menu.id}/add/${correspondingItem.id}`, prefix));
                } else {
                    navigate(AppConfig.getLinkWithPrefix(`/menu/${sortedMenus[0].id}`, prefix), {replace: true});
                }
            }
        } else {
            const correspondingItem = props.items.find((item: Record<string, any>) => {
                const name = item.translations.find((translation: Record<string, any>) => translation.name === productName)?.name ?? item.name;

                return name === productName;
            });

            if (correspondingItem) {
                navigate(AppConfig.getLinkWithPrefix(`/menu/add/${correspondingItem.id}`, prefix));
            } else {
                navigate(AppConfig.getLinkWithPrefix("/", prefix), {replace: true});
            }
        }
    }

    useEffect(() => {
        if (!isMultilevelMenu || sortedMenus.length === 0 || hasRedirected) return;

        const menuIds = sortedMenus.map(menu => menu.id);

        if (!finalMenuId || !menuIds.includes(finalMenuId)) {
            const firstAvailableMenuId = menuIds[0];
            navigate(AppConfig.getLinkWithPrefix(`/menu/${firstAvailableMenuId}`, prefix));
            setHasRedirected(true);
            return;
        }
    }, [isMultilevelMenu, finalMenuId, sortedMenus, prefix, hasRedirected]);

    const navigateToCartPage = () => navigate(AppConfig.getLinkWithPrefix('/cart', prefix));

    const handleCartButtonClick = () => {
        const parsedCurrentType = getParsedSessionOrderType(props.session.order_type);

        if (props.config && props.session.order_type && !StoreUtils.getOrderTypes(props.config).includes(props.session.order_type as OrderType)) {
            setShowUnavailableOrderTypeModal(true);
            return;
        }

        if (props.session.pickup_at && !isAnotherDateAvailable(props.config, props.menus, parsedCurrentType)) {
            setShowUnavailablePickupAtModal(true);
            return;
        }

        handleUpsells();
    }

    const handleUpsells = () => {
        let upsells: UpsellApi[] = [];

        if (props.upsells.length > 0) {
            upsells = UpsellHelper.getValidUpsells(
                props.upsells,
                props.cart,
                props.session.order_type as OrderType,
                props.items,
                props.itemGroups,
                props.availabilities
            );
        }

        if (upsells.length > 0) {
            setValidUpsells(upsells);
        } else {
            navigateToCartPage();
        }
    }

    const scrollToTop = () => {
        if (AppConfig.isWidget()) {
            IframeWidget.scrollTo(0);
        } else {
            window.scrollTo({top: 0, left: 0, behavior: "smooth"});
        }
    }

    const shouldDrawHero = () => {
        if (StoreUtils.isMultistore(props.defaultConfig)) return false;

        if (StoreUtils.isHomepageMenu(props.config)) return true;

        return false;
    }

    const drawComponentsAboveMenuForSmallDevices = () => {
        return (
            <div className="menu-container-with-gap margin-bottom-for-last-child">
                {
                    !showSearchBar && !AppConfig.isNativeMobile() && (
                        <>
                            {shouldDrawHero() && (
                                <Hero config={props.config}/>
                            )}
                        </>
                    )
                }
                {(!AppConfig.isNativeMobile()) && (<MenuAdvertComponent menuAdverts={menuAdverts}/>)}
                {!showSearchBar && (
                    <MenuPromotionsComponent promotions={props.promotions}/>
                )}
            </div>
        )
    }

    const drawComponentsAboveMenuForLargeDevices = () => {
        return (
            <div className="menu-container-with-gap margin-bottom-for-last-child">
                {(!AppConfig.isNativeMobile()) && (
                    <>
                        {shouldDrawHero() && (
                            <Hero config={props.config}/>
                        )}
                    </>
                )}
                {(!AppConfig.isNativeMobile()) && (<MenuAdvertComponent menuAdverts={menuAdverts}/>)}
                <MenuPromotionsComponent promotions={props.promotions}/>
            </div>
        )
    }

    return (
        <FetchData menus order>
            <CartStickyContextPage config={props.config}>
                <div
                    className={`menu-page ${AppConfig.isWidget() ? "menu-page--widget" : ""} ${StoreUtils.isOrderingReadonly(props.config) ? "menu-page--readonly" : ""}`}>
                    {!(StoreUtils.isOrderingReadonly(props.config)) && (
                        <CartStickyComponent goToCart={handleCartButtonClick} showVouchers placement="MENU"
                                             menuId={finalMenuId}/>
                    )}
                    {
                        validUpsells.length > 0 && (
                            <UpsellsModal upsells={validUpsells} onHide={() => setValidUpsells([])}
                                          show={validUpsells.length > 0}/>
                        )
                    }
                    {
                        showUnavailablePickupAtModal && (
                            <UnavailablePickupAtModal show={showUnavailablePickupAtModal}
                                                      onHide={() => setShowUnavailablePickupAtModal(false)}/>
                        )
                    }
                    {
                        showUnavailableOrderTypeModal && (
                            <UnavailableOrderTypeModal show={showUnavailableOrderTypeModal}
                                                       onHide={() => setShowUnavailableOrderTypeModal(false)}/>
                        )
                    }
                    {(!AppConfig.isNativeMobile()) && <MenuAdvertBanner menuAdverts={menuAdverts}/>}
                    <AgeRestrictedModalComponent/>
                    {
                        showVoucherProductsModal && <VoucherProductsModal show={showVoucherProductsModal}
                                                                          onHide={() => setShowVoucherProductsModal(false)}
                                                                          menus={props.menus}
                                                                          promotions={props.promotions}
                                                                          cart={props.cart}
                                                                          availabilities={props.availabilities}
                                                                          session={props.session}
                                                                          isStorePaused={StoreUtils.isPaused(props.config)}
                                                                          items={[]}
                                                                          locale={props.locale}
                                                                          categories={props.categories}
                                                                          config={props.config}
                        />
                    }
                    <ResponsiveComponent small={drawComponentsAboveMenuForSmallDevices()}
                                         large={drawComponentsAboveMenuForLargeDevices()} breakpoint={768}/>
                    <div className={classNames('menu body-container body-container-cart')}>
                        <MenuComponent
                            menuId={isMultilevelMenu ? finalMenuId : undefined}/>
                    </div>
                    {StoreUtils.isOrderingEnabled(props.config) && (
                        <ResponsiveComponent small={
                            <div className="btn-submit-bottom fixed-bottom btn-cart-container">
                                <CartButtonComponent goToCart={handleCartButtonClick} menuId={finalMenuId}/>
                            </div>
                        } large={<></>} breakpoint={992}/>
                    )}
                    <Routes>
                        {
                            isMultilevelMenu ? (
                                <>
                                    <Route path="category/:id"
                                           element={<MenuCategoryPage menuId={finalMenuId}/>}/>
                                    <Route path={`add/:id`} element={<MenuProductPage menuId={finalMenuId}/>}/>
                                </>
                            ) : (
                                <>
                                    <Route path="menu/:menuId/add/:id" element={<RedirectOldMenuAdd/>}/>
                                    <Route path="menu/add/:id" element={<MenuProductPage/>}/>
                                    <Route path="category/:id" element={<MenuCategoryPage/>}/>
                                    <Route path="/menu/category/:id" element={<MenuCategoryPage/>}/>
                                    <Route path="add/:id" element={<MenuProductPage/>}/>
                                </>
                            )
                        }
                    </Routes>
                    {
                        isScrollToTopButtonVisible && (
                            <div className={"navigate-bottom"}>
                                <button className={"btn btn-default btn-icon btn-lg"} onClick={scrollToTop}
                                        aria-label={t("modules.menu.action.scroll_to_top.title")}
                                >
                                    <ArrowTopSVG aria-hidden="true"/>
                                </button>
                            </div>
                        )
                    }
                    <CallWaiterStickyButton/>
                </div>
            </CartStickyContextPage>
        </FetchData>
    )
};

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    defaultConfig: getStoreDefaultConfig(state.config),
    promotions: getPromotions(state.menu),
    session: getSession(state.session),
    cart: getCart(state.cart),
    items: getItems(state.menu),
    itemGroups: getItemGroups(state.menu),
    menus: getMenus(state.menu),
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
    storeLayoutComponentsConfig: getStoreLayoutComponentsConfig(state.config),
    categories: getCategories(state.menu),
    upsells: getUpsells(state.menu)
})

const connector = connect(mapStateToProps);
export default connector(MenuPage);
